import { Link } from "react-router-dom";
import { ArrowRight, Calendar, Clock, ChevronLeft, MapPin, CheckCircle2 } from "lucide-react";
import Seo from "@/components/Seo";

const PATH = "/blog/how-to-write-mice-rfp-asia";
const TITLE = "How to Write a MICE RFP That Gets You the Best Asia Proposals";
const DESCRIPTION =
  "A practical guide for corporate MICE planners — the seven sections every Asia MICE RFP should contain, common mistakes to avoid, and a downloadable RFP checklist.";
const PUBLISHED_ISO = "2026-02-17";
const PUBLISHED_LABEL = "Feb 17, 2026";
const AUTHOR = "Trawelldmc Team";
const IMAGE =
  "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?crop=entropy&cs=srgb&fm=webp&w=1600&q=80";

const BREADCRUMB = [
  { name: "Home", path: "/" },
  { name: "Blog", path: "/blog" },
  { name: "How to Write a MICE RFP", path: PATH },
];

const ARTICLE_JSONLD = {
  "@context": "https://schema.org",
  "@type": "Article",
  headline: TITLE,
  description: DESCRIPTION,
  image: IMAGE,
  datePublished: PUBLISHED_ISO,
  dateModified: PUBLISHED_ISO,
  author: { "@type": "Organization", name: AUTHOR, url: "https://www.trawelldmc.com" },
  publisher: {
    "@type": "Organization",
    name: "Trawelldmc",
    logo: { "@type": "ImageObject", url: "https://www.trawelldmc.com/trawelldmc-logo-hd.png" },
  },
  mainEntityOfPage: { "@type": "WebPage", "@id": `https://www.trawelldmc.com${PATH}` },
};

const SECTIONS = [
  {
    title: "Programme Overview",
    copy:
      "Open with a one-paragraph narrative answer: what is this event, who is it for, and what does success look like? Skip the corporate marketing — DMCs need to feel the brief, not read it. Include the high-level theme or storyline if you have one.",
  },
  {
    title: "Headline Logistics",
    copy:
      "Number of pax (with confirmed vs flex), preferred dates (with hard non-movable constraints flagged), destination preferences (with ranked alternatives), and approximate budget envelope per pax. Vague numbers produce vague proposals — be specific.",
  },
  {
    title: "Delegate Profile",
    copy:
      "Passport mix, dietary needs (e.g. Halal, Jain, vegetarian percentage), language requirements, accessibility needs, age skew, and pre/post-event extension appetite. DMCs price visas, F&amp;B and content very differently depending on this.",
  },
  {
    title: "Mandatory Inclusions",
    copy:
      "Hotel class (be specific: 5★ international chain, beachfront, conference rooms required, etc.), gala dinner specifications, AV technical requirements, simultaneous interpretation languages, branded production scope, transfer logic.",
  },
  {
    title: "Content &amp; Story",
    copy:
      "What &lsquo;wow&rsquo; moments are non-negotiable? Sunset cruise? Cultural welcome? Drone show? List the experiences you&apos;d like the DMC to source — but leave room for creative alternatives the DMC can propose locally.",
  },
  {
    title: "Sustainability &amp; Reporting",
    copy:
      "If your procurement team requires a CO₂ estimate, list it explicitly. Ask the DMC for their sustainability supplier criteria, carbon offset partners, and post-event reporting format. This is now a tier-1 RFP requirement.",
  },
  {
    title: "Process &amp; Timeline",
    copy:
      "Proposal submission deadline, contracting timeline, payment milestones, and the named point of contact. Specify deliverables clearly: total cost summary, line-item breakdown, photo references for proposed venues, and 2–3 hotel options per category.",
  },
];

const MISTAKES = [
  "Sending the same brief to 8 DMCs and choosing on price alone — you lose creativity.",
  "Not stating the budget envelope, then rejecting proposals because they&apos;re too expensive.",
  "Demanding a fixed gala venue without checking inventory — many iconic venues are booked 12+ months out.",
  "Asking for &lsquo;exclusive&rsquo; experiences without specifying what would count as exclusive for your audience.",
  "Forgetting to include the visa-jurisdiction list — surfacing it 4 weeks before departure causes chaos.",
];

export default function HowToWriteMiceRfp() {
  return (
    <div data-testid="how-to-write-mice-rfp">
      <Seo
        path={PATH}
        title={`${TITLE} | Trawelldmc Insights`}
        description={DESCRIPTION}
        type="article"
        image={IMAGE}
        breadcrumb={BREADCRUMB}
        extraJsonLd={ARTICLE_JSONLD}
      />

      <section className="bg-[#0B192C] text-white">
        <div className="max-w-3xl mx-auto px-6 md:px-10 py-20 md:py-24">
          <Link to="/blog" className="inline-flex items-center gap-1.5 text-sm text-white/60 hover:text-white mb-8">
            <ChevronLeft className="h-4 w-4" /> Back to Blog
          </Link>
          <div className="overline !text-[#FF5238] mb-5">MICE · Practical Guide</div>
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-light tracking-tight leading-[1.08]">
            {TITLE}
          </h1>
          <div className="mt-8 flex items-center gap-5 text-sm text-white/60 flex-wrap">
            <span className="inline-flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" /> {PUBLISHED_LABEL}</span>
            <span className="inline-flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> 9 min read</span>
            <span className="inline-flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" /> Asia DMC</span>
            <span>By {AUTHOR}</span>
          </div>
        </div>
      </section>

      <section>
        <div className="max-w-5xl mx-auto px-6 md:px-10 -mt-10 mb-12">
          <img
            src={IMAGE}
            alt="MICE RFP planning guide — Trawelldmc"
            width="1600"
            height="900"
            loading="lazy"
            decoding="async"
            className="w-full aspect-[16/9] object-cover rounded-md shadow-xl"
          />
        </div>
      </section>

      <article className="max-w-3xl mx-auto px-6 md:px-10 pb-24">
        <p className="text-xl text-slate-700 leading-relaxed font-light">
          A well-written MICE RFP is the difference between getting three
          generic proposals and three creative, costed, on-brief offers from
          DMCs who actually understand your event. After two decades of
          responding to thousands of Asia MICE RFPs, here&apos;s the seven-part
          structure we recommend — plus the five mistakes that cost planners
          time and money.
        </p>

        <h2 className="font-heading text-3xl md:text-4xl font-light tracking-tight text-[#0B192C] mt-12 mb-5">
          The Seven Sections Every Asia MICE RFP Should Contain
        </h2>

        {SECTIONS.map((s, i) => (
          <div key={s.title}>
            <h3 className="font-heading text-2xl md:text-3xl font-medium tracking-tight text-[#0B192C] mt-10 mb-3 flex items-baseline gap-3">
              <span className="text-[#FF5238]">{String(i + 1).padStart(2, "0")}.</span>
              {s.title}
            </h3>
            <p className="text-slate-700 leading-relaxed" dangerouslySetInnerHTML={{ __html: s.copy }} />
          </div>
        ))}

        <h2 className="font-heading text-3xl md:text-4xl font-light tracking-tight text-[#0B192C] mt-16 mb-5">
          Five Mistakes That Sabotage Asia MICE RFPs
        </h2>
        <ul className="text-slate-700 leading-relaxed mt-4 space-y-3 list-none pl-0">
          {MISTAKES.map((m) => (
            <li key={m} className="flex gap-3">
              <CheckCircle2 className="h-5 w-5 text-[#FF5238] flex-shrink-0 mt-1" />
              <span dangerouslySetInnerHTML={{ __html: m }} />
            </li>
          ))}
        </ul>

        <h2 className="font-heading text-3xl md:text-4xl font-light tracking-tight text-[#0B192C] mt-16 mb-5">
          One More Thing: How Many DMCs Should You Shortlist?
        </h2>
        <p className="text-slate-700 leading-relaxed">
          Three. Four at most. Sending the same brief to eight DMCs guarantees
          two outcomes: thin, comparison-shopped pricing — and a much lower
          chance that any single DMC will invest in creative differentiation.
          Pick three operators with strong references in the destination,
          brief them deeply, and award decisively. The ROI on this approach
          beats blind bidding every time.
        </p>

        <div className="mt-12 p-8 bg-[#0B192C] text-white rounded-md">
          <div className="overline !text-[#FF5238] mb-3">Brief us</div>
          <h3 className="font-heading text-2xl font-medium tracking-tight">
            Ready to send a brief Trawelldmc can answer?
          </h3>
          <p className="text-white/80 mt-3 leading-relaxed">
            Use our inquiry form or talk to TrawellAI for an instant first
            quote — we respond within 48 hours with a costed, creative
            proposal across any of our 11 Asia DMC markets.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/inquiry?type=MICE" className="btn-primary inline-flex">
              Submit an RFP <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/quote" className="btn-secondary !bg-white/10 !border-white/20 !text-white hover:!bg-white/20">
              Try AI First Quote
            </Link>
          </div>
        </div>
      </article>
    </div>
  );
}
